import type { Variant } from "./api";

/** Facets choose an installed variant, retaining the normal authoritative setup path. */
export function VariantControls({
    variants,
    current,
    onChange,
}: {
    variants: Variant[];
    current: Variant | undefined;
    onChange: (id: string) => void;
}) {
    const playable = variants.filter((v) => v.playable);
    const options = current?.options;
    const signatures = playable.map((v) =>
        JSON.stringify([
            v.playerCount,
            [...(v.options ?? [])]
                .sort((a, b) => a.key.localeCompare(b.key))
                .map((o) => [o.key, o.value]),
        ]),
    );
    const faceted =
        options?.length &&
        new Set(signatures).size === playable.length &&
        playable.every(
            (v) =>
                v.options?.length === options.length &&
                options.every((o) =>
                    v.options?.some(
                        (x) => x.key === o.key && x.label === o.label,
                    ),
                ),
        );
    if (!current || !faceted)
        return (
            <label className="club-field">
                <span>Variant &amp; player count</span>
                <select
                    value={current?.id ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                >
                    {playable.map((v) => (
                        <option key={v.id} value={v.id}>
                            {v.label}
                            {/\bplayers?\b/i.test(v.label)
                                ? ""
                                : ` · ${v.playerCount} players`}
                        </option>
                    ))}
                </select>
            </label>
        );
    const counts = [...new Set(playable.map((v) => v.playerCount))].sort(
        (a, b) => a - b,
    );
    const changeCount = (count: number) => {
        const next =
            playable.find(
                (v) =>
                    v.playerCount === count &&
                    options.every((o) =>
                        v.options?.some(
                            (x) => x.key === o.key && x.value === o.value,
                        ),
                    ),
            ) ?? playable.find((v) => v.playerCount === count);
        if (next) onChange(next.id);
    };
    return (
        <>
            <label className="club-field">
                <span>Player count</span>
                <select
                    value={current.playerCount}
                    onChange={(e) => changeCount(Number(e.target.value))}
                >
                    {counts.map((n) => (
                        <option key={n} value={n}>
                            {n} players
                        </option>
                    ))}
                </select>
            </label>
            {options.map((option) => {
                const eligible = playable.filter(
                    (v) =>
                        v.playerCount === current.playerCount &&
                        options.every(
                            (o) =>
                                o.key === option.key ||
                                v.options?.some(
                                    (x) =>
                                        x.key === o.key && x.value === o.value,
                                ),
                        ),
                );
                const values = [
                    ...new Map(
                        eligible.flatMap((v) =>
                            v
                                .options!.filter((o) => o.key === option.key)
                                .map((o) => [o.value, o]),
                        ),
                    ).values(),
                ];
                return (
                    <label className="club-field" key={option.key}>
                        <span>{option.label}</span>
                        <select
                            value={option.value}
                            onChange={(e) => {
                                const next = eligible.find((v) =>
                                    v.options?.some(
                                        (o) =>
                                            o.key === option.key &&
                                            o.value === e.target.value,
                                    ),
                                );
                                if (next) onChange(next.id);
                            }}
                        >
                            {values.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.valueLabel}
                                </option>
                            ))}
                        </select>
                    </label>
                );
            })}
        </>
    );
}
